import { useEffect, useState } from 'react';
import CampoImporto from '../components/CampoImporto';
import { Link, useSearchParams } from 'react-router-dom';
import { fatturazionIApi, commesseApi, clientiApi, ordiniApi } from '../api';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import RiepilogoMensile from '../components/RiepilogoMensile';
import DaRiaddebitare from '../components/DaRiaddebitare';
import InRitardo from '../components/InRitardo';
import DaMandare from '../components/DaMandare';
import SelettoreMese, { meseDiLavoro, fineMese, nomeMese } from '../components/SelettoreMese';
import SceltaMese from '../components/SceltaMese';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const STATI = ['da_emettere', 'emessa', 'pagata', 'annullata'];
const empty = { commessa_id: '', cliente_id: '', numero_fattura: '', descrizione: '', importo: '', data_emissione: '', data_scadenza: '', data_pagamento: '', competenza: '', stato: 'da_emettere', note: '', ordine_id: '' };

const oggi = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const giorniA = d => d ? Math.round((new Date(d) - oggi()) / 86400000) : null;

/* ────────────────────────────────────────────────────────────────────────────
   Vista "Da emettere": separa ciò che il sistema sa pianificare (fatturazione
   periodica) da ciò che dipende da un avanzamento che devi registrare tu.
   ──────────────────────────────────────────────────────────────────────────── */
function DaEmettere({ commesse, onVaiA, onVaiADaMandare }) {
  // Il mese di competenza: si fattura il mese passato nei primi giorni di
  // quello nuovo, quindi il taglio è "tutto ciò che è dovuto entro fine mese
  // selezionato", arretrati compresi.
  const iniziale = meseDiLavoro();
  const [anno, setAnno] = useState(iniziale.anno);
  const [mese, setMese] = useState(iniziale.mese);
  const limite = new Date(`${fineMese(anno, mese)}T23:59:59`);

  const conResiduo = commesse.filter(c => (parseFloat(c.da_emettere) || 0) > 0.005);
  const entroIlMese = c => !c.prossima_fattura || new Date(c.prossima_fattura) <= limite;
  const periodiche = conResiduo.filter(c => c.periodicita_mesi).sort(
    (a, b) => new Date(a.prossima_fattura || 0) - new Date(b.prossima_fattura || 0));
  const periodicheFuori = periodiche.filter(c => !entroIlMese(c));
  const periodicheNelMese = periodiche.filter(entroIlMese);
  const aSal = conResiduo.filter(c => !c.periodicita_mesi);

  const totP = periodiche.reduce((s, c) => s + parseFloat(c.da_emettere), 0);
  const totS = aSal.reduce((s, c) => s + parseFloat(c.da_emettere), 0);
  const scadute = periodiche.filter(c => giorniA(c.prossima_fattura) < 0);
  const imminenti = periodiche.filter(c => { const g = giorniA(c.prossima_fattura); return g >= 0 && g <= 15; });

  // Competenza: anno di avvio della commessa, la stessa regola usata in Dashboard
  const annoDi = c => new Date(c.data_inizio || c.created_at).getFullYear();
  const annoCorrente = new Date().getFullYear();
  const daCorrente = conResiduo.filter(c => annoDi(c) === annoCorrente);
  const daPrecedenti = conResiduo.filter(c => annoDi(c) < annoCorrente);
  const somma = arr => arr.reduce((s, c) => s + parseFloat(c.da_emettere), 0);
  // Ripartizione per anno dei residui più vecchi, dal più remoto
  const perAnnoPrec = Object.entries(
    daPrecedenti.reduce((acc, c) => {
      const a = annoDi(c);
      acc[a] = (acc[a] || 0) + parseFloat(c.da_emettere);
      return acc;
    }, {})
  ).sort((a, b) => a[0] - b[0]);

  const Riga = ({ c, mostraScadenza }) => {
    const g = giorniA(c.prossima_fattura);
    const cls = g === null ? '' : g < 0 ? 'bg-red-50/70' : g <= 15 ? 'bg-amber-50/70' : '';
    return (
      <tr className={`border-b last:border-0 hover:bg-gray-100 ${cls}`}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            <Link to={`/commesse/${c.id}`} className="font-mono text-xs text-brand-600 hover:underline">{c.numero}</Link>
            {annoDi(c) < annoCorrente && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                    title={`Residuo dell'anno ${annoDi(c)}`}>
                {annoDi(c)}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 truncate max-w-[260px]">{c.titolo}</div>
        </td>
        <td className="px-4 py-3 text-gray-600">{c.cliente_nome}</td>
        <td className="px-4 py-3 text-right font-medium text-yellow-700">{fmt(c.da_emettere)}</td>
        <td className="px-4 py-3 text-xs text-gray-500">{c.tipo_fatturazione || '—'}</td>
        {mostraScadenza && (
          <td className="px-4 py-3 text-xs">
            {c.prossima_fattura ? (
              <>
                <div className={g < 0 ? 'text-red-600 font-semibold' : g <= 15 ? 'text-amber-700 font-medium' : 'text-gray-600'}>
                  {fmtDate(c.prossima_fattura)}
                </div>
                <div className="text-[11px] text-gray-500">
                  {g < 0 ? `in ritardo di ${Math.abs(g)} gg` : g === 0 ? 'da emettere oggi' : `fra ${g} gg`}
                </div>
              </>
            ) : <span className="text-gray-400">—</span>}
          </td>
        )}
        <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(c.ultima_emissione)}</td>
      </tr>
    );
  };

  const totMese = somma(periodicheNelMese);
  // Il "da emettere" è la somma di due cose diverse: le fatture già decise e
  // in coda, e il residuo del contratto che nessuno ha ancora deciso come
  // fatturare (la riga "Da fatturare", che si sbriga dalla scheda "Da mandare").
  const totResiduo = conResiduo.reduce((s2, c) => s2 + (parseFloat(c.residuo_da_decidere) || 0), 0);
  const totDeciso = (totP + totS) - totResiduo;

  return (
    <div className="space-y-5">
      <SelettoreMese anno={anno} mese={mese} onChange={(a, m) => { setAnno(a); setMese(m); }}>
        <div className="ml-auto text-sm text-gray-600">
          Dovuto entro fine {nomeMese(mese)}:{' '}
          <strong className="text-amber-700">{fmt(totMese)}</strong>
        </div>
      </SelettoreMese>

      {/* Note di credito in coda: fuori dai totali da incassare, ma da non
          dimenticare — sono il seguito di una rettifica di SAL. */}
      {(() => {
        const conNc = commesse.filter(c => (parseFloat(c.nc_da_emettere) || 0) < -0.005);
        if (!conNc.length) return null;
        return (
          <div className="card bg-red-50/60 border-red-200">
            <div className="text-sm font-semibold text-red-800">
              Note di credito da emettere: {conNc.length}
            </div>
            <div className="text-xs text-red-700 mt-1 space-y-0.5">
              {conNc.map(c => (
                <div key={c.id}>
                  <Link to={`/commesse/${c.id}`} className="font-mono hover:underline">{c.numero}</Link>
                  {' '}{c.cliente_nome} · <strong>{fmt(c.nc_da_emettere)}</strong>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-red-600/80 mt-1.5">
              Emettile in Fatture in Cloud e spuntale nel Riepilogo mese.
            </p>
          </div>
        );
      })()}

      {/* Quanto resta da emettere, per competenza */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Totale da emettere</div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">{fmt(totP + totS)}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {conResiduo.length} commesse · tutte le annualità
          </div>
          {/* Questo numero contiene anche il residuo ancora da decidere, che è
              quello della scheda "Da mandare": senza dirlo i due totali
              sembravano in contraddizione. */}
          <div className="text-[11px] text-gray-500 mt-1.5 border-t pt-1.5 leading-snug">
            <div>fatture già decise, in coda: <strong>{fmt(totDeciso)}</strong></div>
            <div>
              residuo ancora da decidere: <strong>{fmt(totResiduo)}</strong>
              {onVaiADaMandare && totResiduo > 0.005 && (
                <button type="button" className="ml-1 text-brand-600 hover:underline"
                        onClick={onVaiADaMandare}>→ Da mandare</button>
              )}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Di competenza {annoCorrente}</div>
          <div className="text-2xl font-bold text-brand-600 mt-1">{fmt(somma(daCorrente))}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            {daCorrente.length} commesse avviate nel {annoCorrente}
          </div>
        </div>
        <div className={`card ${daPrecedenti.length ? 'bg-red-50/60 border-red-200 cursor-pointer hover:shadow-md transition-shadow' : ''}`}
             onClick={daPrecedenti.length ? onVaiA : undefined}
             title={daPrecedenti.length ? 'Vedi quali commesse e decidi se chiuderle' : undefined}>
          <div className="text-sm text-gray-500">
            Residuo anni precedenti
            {daPrecedenti.length > 0 && <span className="text-xs text-red-600 ml-1">· apri</span>}
          </div>
          <div className={`text-2xl font-bold mt-1 ${daPrecedenti.length ? 'text-red-600' : 'text-gray-400'}`}>
            {fmt(somma(daPrecedenti))}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {daPrecedenti.length === 0
              ? 'Nessuno strascico: tutto chiuso'
              : perAnnoPrec.map(([a, v]) => `${a}: ${fmt(v)}`).join(' · ')}
          </div>
        </div>
      </div>

      {/* Quando vanno emesse */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card"><div className="text-sm text-gray-500">In ritardo</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{scadute.length}</div>
          <div className="text-xs text-gray-400 mt-0.5">{fmt(somma(scadute))}</div></div>
        <div className="card"><div className="text-sm text-gray-500">Entro 15 giorni</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{imminenti.length}</div>
          <div className="text-xs text-gray-400 mt-0.5">{fmt(somma(imminenti))}</div></div>
        <div className="card"><div className="text-sm text-gray-500">In attesa di SAL</div>
          <div className="text-2xl font-bold text-gray-700 mt-1">{aSal.length}</div>
          <div className="text-xs text-gray-400 mt-0.5">{fmt(totS)}</div></div>
      </div>

      {/* Periodiche: il sistema calcola la scadenza */}
      <div className="card p-0 overflow-x-auto">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">
            🗓 Fatturazione periodica — dovuta entro {nomeMese(mese)} {anno}: {periodicheNelMese.length} commesse · {fmt(totMese)}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            La scadenza è calcolata dall'ultima fattura emessa più la periodicità concordata.
            {periodicheFuori.length > 0 && (
              <> Altre <strong>{periodicheFuori.length}</strong> commesse ({fmt(somma(periodicheFuori))})
                 scadono dopo: cambia mese per vederle.</>
            )}
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left text-xs text-gray-500">
              <th className="px-4 py-2">Commessa</th><th className="px-4 py-2">Cliente</th>
              <th className="px-4 py-2 text-right">Da emettere</th><th className="px-4 py-2">Modalità</th>
              <th className="px-4 py-2">Prossima fattura</th><th className="px-4 py-2">Ultima emessa</th>
            </tr>
          </thead>
          <tbody>
            {periodicheNelMese.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                Nessuna fattura periodica dovuta entro {nomeMese(mese)} {anno}
              </td></tr>
            )}
            {periodicheNelMese.map(c => <Riga key={c.id} c={c} mostraScadenza />)}
          </tbody>
        </table>
      </div>

      {/* A SAL: dipendono da un avanzamento da registrare */}
      <div className="card p-0 overflow-x-auto">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">📋 A stato avanzamento / milestone — {aSal.length} commesse · {fmt(totS)}</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Qui la fattura dipende da un evento (SAL, audit, acconto): la emetti tu quando l'avanzamento è maturato.
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left text-xs text-gray-500">
              <th className="px-4 py-2">Commessa</th><th className="px-4 py-2">Cliente</th>
              <th className="px-4 py-2 text-right">Da emettere</th><th className="px-4 py-2">Modalità</th>
              <th className="px-4 py-2">Ultima emessa</th>
            </tr>
          </thead>
          <tbody>
            {aSal.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Nessuna commessa in attesa</td></tr>}
            {aSal.map(c => <Riga key={c.id} c={c} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AvanzamentoCommessa({ commessa }) {
  const budget = parseFloat(commessa.budget) || 0;
  // Sul budget si misura solo il corrispettivo: le spese riaddebitate si
  // aggiungono alla fattura ma non consumano il contratto.
  const emesse = parseFloat(commessa.fatturato_contratto ?? commessa.fatturato) || 0;
  const spese = parseFloat(commessa.fatturato_spese) || 0;
  const pagate = parseFloat(commessa.pagato) || 0;
  const daEmettere = parseFloat(commessa.da_emettere) || 0;
  // Per il residuo del budget contano solo le righe di contratto
  const daEmettereContratto = parseFloat(commessa.da_emettere_contratto ?? commessa.da_emettere) || 0;
  const pctPagato = budget > 0 ? Math.min(100, (pagate / budget) * 100) : 0;
  const pctEmesso = budget > 0 ? Math.min(100, (emesse / budget) * 100) : 0;
  // Serve anche che il budget risulti coperto: senza fatture da_emettere è 0
  // ma la commessa non è affatto conclusa.
  const completa = budget > 0 && daEmettereContratto < 0.01 && emesse >= budget - 0.01;

  return (
    <div className={`card hover:shadow-md transition-shadow ${completa ? 'bg-green-50/70 border-green-200' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <Link to={`/commesse/${commessa.id}`} className="font-semibold text-gray-900 hover:text-brand-600 hover:underline">
            {commessa.numero} — {commessa.titolo}
          </Link>
          <div className="text-xs text-gray-500 mt-0.5">{commessa.cliente_nome}</div>
        </div>
        <div className="text-right text-xs text-gray-500 shrink-0 ml-4">
          Budget: <span className="font-semibold text-gray-700">{fmt(budget)}</span>
          {completa && <div className="text-green-700 font-semibold mt-0.5">✓ Interamente fatturata</div>}
        </div>
      </div>

      <div className="relative w-full bg-gray-200 rounded-full h-4 mb-2 overflow-hidden">
        <div className="absolute left-0 top-0 h-4 rounded-full bg-green-500 transition-all" style={{ width: `${pctPagato}%` }} />
        <div className="absolute left-0 top-0 h-4 rounded-full bg-brand-400 opacity-60 transition-all" style={{ width: `${pctEmesso}%` }} />
        <span className="absolute right-2 top-0 h-4 flex items-center text-xs font-medium text-gray-700">
          {Math.round(pctEmesso)}%
        </span>
      </div>

      <div className={`grid gap-2 text-xs mt-1 ${spese > 0 ? 'grid-cols-5' : 'grid-cols-4'}`}>
        <div><div className="text-gray-400">Pagato</div><div className="font-semibold text-green-600">{fmt(pagate)}</div></div>
        <div><div className="text-gray-400">Emesso</div><div className="font-semibold text-brand-600">{fmt(emesse)}</div></div>
        <div><div className="text-gray-400">Da emettere</div><div className="font-semibold text-yellow-600">{fmt(daEmettere)}</div></div>
        <div><div className="text-gray-400">Residuo</div>
          <div className="font-semibold text-gray-500">{fmt(Math.max(0, budget - emesse - daEmettereContratto))}</div></div>
        {spese > 0 && (
          <div><div className="text-gray-400">Spese riaddebitate</div>
            <div className="font-semibold text-gray-600">{fmt(spese)}</div></div>
        )}
      </div>
    </div>
  );
}

export default function Fatturazioni() {
  const [rows, setRows] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  // Ricerca libera su "Tutte le fatture": 266 righe, e quelle non ancora
  // emesse finiscono in fondo perché non hanno una data. Cercarle scorrendo
  // non è cercare.
  const [cerca, setCerca] = useState('');
  // La vista può arrivare dall'indirizzo: dalla dashboard si salta diretti
  // su "In ritardo" cliccando il numero.
  const [params, setParams] = useSearchParams();
  const [vista, setVista] = useState(params.get('vista') || 'da_emettere');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // Ordini della commessa scelta nel modulo: servono a citare in fattura
  // quello giusto e a vedere quanta capienza resta.
  const [ordini, setOrdini] = useState(null);

  useEffect(() => {
    if (!modal || !form.commessa_id) { setOrdini(null); return; }
    let vivo = true;
    ordiniApi.perCommessa(form.commessa_id).then(d => { if (vivo) setOrdini(d); }).catch(() => {});
    return () => { vivo = false; };
  }, [modal, form.commessa_id]);

  // Si scaricano tutte le fatture una volta sola e si filtra qui: i filtri
  // diventano istantanei e, soprattutto, si può dire a chi non trova una
  // fattura QUALE filtro gliela sta nascondendo.
  const load = () => fatturazionIApi.list({}).then(setRows);
  const loadCommesse = () => commesseApi.list({ cliente_id: filtroCliente || undefined }).then(setCommesse);
  useEffect(() => { load(); }, []);
  useEffect(() => { loadCommesse(); }, [filtroCliente]);
  useEffect(() => { clientiApi.list().then(setClienti); }, []);

  const openNew = () => { setForm(empty); setModal({ mode: 'new' }); };
  const openEdit = (r) => {
    setForm({ ...r, data_emissione: r.data_emissione?.slice(0,10)||'', data_scadenza: r.data_scadenza?.slice(0,10)||'', data_pagamento: r.data_pagamento?.slice(0,10)||'', competenza: r.competenza?.slice(0,7)||'' });
    setModal({ mode: 'edit', id: r.id });
  };

  const save = async () => {
    if (!form.descrizione?.trim()) return alert('Scrivi una descrizione: è quello che si legge in elenco.');
    if (!form.commessa_id && !form.cliente_id)
      return alert('Scegli la commessa, oppure il cliente se la fattura non ne ha una.');
    setSaving(true);
    try {
      if (modal.mode === 'new') await fatturazionIApi.create(form);
      else await fatturazionIApi.update(modal.id, form);
      setModal(null); load(); loadCommesse();
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm('Eliminare?')) return;
    await fatturazionIApi.delete(id); load(); loadCommesse();
  };

  // Si cerca in tutto quello che si legge nella riga, importo compreso:
  // "10750", "10.750", "ferretti" o "FS0226" devono portare allo stesso posto.
  const parole = cerca.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const testoDi = (r) => [r.numero_fattura, r.commessa_numero, r.commessa_titolo, r.cliente_nome,
                          r.descrizione, r.note, r.stato, r.stato?.replace(/_/g, ' '), r.ordine_numero,
                          r.importo, Number(r.importo).toLocaleString('it-IT')]
    .filter(x => x !== null && x !== undefined).join(' ').toLowerCase();
  const passaTesto = (r) => !parole.length || parole.every(p2 => testoDi(r).includes(p2));
  // Una fattura fuori commessa è intestata al cliente direttamente: il filtro
  // deve guardare lì, altrimenti sparisce appena si sceglie un cliente.
  const passaCliente = (r) => !filtroCliente || (r.commessa_id
    ? commesse.some(c => c.id === r.commessa_id)
    : String(r.cliente_id || '') === String(filtroCliente));
  const passaStato = (r) => !filtroStato || r.stato === filtroStato;

  const righeCliente = rows.filter(r => passaTesto(r) && passaCliente(r) && passaStato(r));
  // Quando non si trova niente: cos'è che lo sta nascondendo? Senza dirlo si
  // cerca tre volte e si conclude che la fattura non c'è.
  const soloTesto = rows.filter(passaTesto);
  const nascosteDalCliente = soloTesto.filter(r => !passaCliente(r) && passaStato(r)).length;
  const nascosteDalloStato = soloTesto.filter(r => passaCliente(r) && !passaStato(r)).length;
  const nascosteDaEntrambi = soloTesto.filter(r => !passaCliente(r) && !passaStato(r)).length;
  const totale = righeCliente.reduce((s, r) => s + parseFloat(r.importo), 0);
  const conFatture = commesse.filter(c => parseFloat(c.budget) > 0);
  const f = (k) => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Fatturazione</h1>
        <button className="btn-primary" onClick={openNew}>+ Aggiungi</button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          {[['mese', 'Riepilogo mese'], ['da_emettere', 'Da emettere'],
            ['da_mandare', 'Da mandare'],
            ['ritardo', 'In ritardo'],
            ['riaddebiti', 'Spese e rendiconti'],
            ['tabella', 'Tutte le fatture'], ['commesse', 'Per commessa']].map(([v, l], i) => (
            <button key={v} onClick={() => setVista(v)}
              className={`px-3 py-1.5 text-sm ${i > 0 ? 'border-l border-gray-300' : ''} ${vista === v ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {l}
            </button>
          ))}
        </div>

        <select className="input py-1 text-sm max-w-[240px]" value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          <option value="">Tutti i clienti</option>
          {clienti.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale || c.nome}</option>)}
        </select>

        {vista === 'tabella' && (
          <>
            <input className="input py-1 text-sm max-w-[260px]"
                   placeholder="Cerca numero, commessa, cliente, importo…"
                   value={cerca} onChange={e => setCerca(e.target.value)} />
            {cerca && (
              <button className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                      onClick={() => setCerca('')}>azzera</button>
            )}
            {['', ...STATI].map(s => (
              <button key={s} onClick={() => setFiltroStato(s)}
                className={`btn-sm btn ${filtroStato === s ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                {s ? s.replace(/_/g,' ') : 'Tutte'}
              </button>
            ))}
            <span className="ml-auto text-sm text-gray-500">
              {righeCliente.length} {righeCliente.length === 1 ? 'fattura' : 'fatture'} ·
              Totale: <strong>{fmt(totale)}</strong>
            </span>
          </>
        )}
      </div>

      {vista === 'mese' && (
        <RiepilogoMensile onCambiato={() => { load(); loadCommesse(); }}
                          onVaiARiaddebiti={() => setVista('riaddebiti')} />
      )}

      {vista === 'da_emettere' && (
        <DaEmettere commesse={commesse} onVaiA={() => setVista('ritardo')}
                    onVaiADaMandare={() => setVista('da_mandare')} />
      )}

      {/* Il lavoro a corpo non passa dai rendiconti: qui si decide quanto
          fatturare e in che mese, commessa per commessa. */}
      {vista === 'da_mandare' && (
        <DaMandare clienteId={filtroCliente} onCambiato={() => { load(); loadCommesse(); }} />
      )}

      {/* Cosa è fermo: residui di anni precedenti e fatture in coda scadute,
          con la chiusura della commessa a portata di clic. */}
      {vista === 'ritardo' && (
        <InRitardo clienteId={filtroCliente} onCambiato={() => { load(); loadCommesse(); }} />
      )}

      {/* Spese anticipate e rendiconti aperti: cosa c'è da fatturare che non è
          ancora una fattura, raccolto per commessa. */}
      {vista === 'riaddebiti' && (
        <DaRiaddebitare clienteId={filtroCliente} onCambiato={() => { load(); loadCommesse(); }} />
      )}

      {vista === 'tabella' && (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-xs text-gray-500 text-left">
                <th className="px-4 py-3">N. Fattura</th><th className="px-4 py-3">Commessa</th>
                <th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Descrizione</th>
                <th className="px-4 py-3">Ordine</th>
                <th className="px-4 py-3 text-right">Importo</th><th className="px-4 py-3">Emissione</th>
                <th className="px-4 py-3">Pagamento</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {righeCliente.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                  {soloTesto.length === 0 ? (
                    <span className="text-gray-400">
                      Nessuna fattura{parole.length ? ' corrisponde alla ricerca' : ''}.
                    </span>
                  ) : (
                    <>
                      <div className="font-medium text-gray-700">
                        {soloTesto.length === 1
                          ? 'C\'è 1 fattura che corrisponde, ma i filtri la nascondono.'
                          : `Ci sono ${soloTesto.length} fatture che corrispondono, ma i filtri le nascondono.`}
                      </div>
                      <div className="text-xs mt-1">
                        {nascosteDalCliente > 0 && <>{nascosteDalCliente} nascoste dal filtro cliente · </>}
                        {nascosteDalloStato > 0 && <>{nascosteDalloStato} dal filtro stato · </>}
                        {nascosteDaEntrambi > 0 && <>{nascosteDaEntrambi} da entrambi · </>}
                        <button className="text-brand-600 hover:underline font-medium"
                                onClick={() => { setFiltroCliente(''); setFiltroStato(''); }}>
                          mostrale tutte
                        </button>
                      </div>
                    </>
                  )}
                </td></tr>
              )}
              {righeCliente.map(r => (
                <tr key={r.id} className={`border-b last:border-0 hover:bg-gray-50 ${r.stato === 'da_emettere' ? 'bg-yellow-50/50' : ''}`}>
                  <td className="px-4 py-3 font-mono text-gray-600">{r.numero_fattura || '—'}</td>
                  <td className="px-4 py-3">
                    {/* Senza commessa non c'è niente da aprire: la fattura sta
                        in piedi da sola, intestata al cliente. */}
                    {r.commessa_id
                      ? <Link to={`/commesse/${r.commessa_id}`} className="text-brand-600 hover:underline">{r.commessa_numero}</Link>
                      : <span className="text-xs text-gray-400">fuori commessa</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.cliente_nome}</td>
                  <td className="px-4 py-3">
                    {r.tipo_documento === 'nota_credito' && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 mr-1.5">
                        NOTA DI CREDITO
                      </span>
                    )}
                    {r.descrizione}
                  </td>
                  {/* Ordine citato: dove il cliente ne ha emesso uno, una
                      fattura che non lo riporta torna indietro. */}
                  <td className="px-4 py-3 text-xs">
                    {r.ordine_numero
                      ? <span className="font-mono text-gray-600" title={r.ordine_riferimento || ''}>{r.ordine_numero}</span>
                      : r.ordine_mancante
                        ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">da collegare</span>
                        : <span className="text-gray-300">—</span>}
                  </td>
                  <td className={`px-4 py-3 font-medium text-right ${Number(r.importo) < 0 ? 'text-red-600' : ''}`}>
                    {fmt(r.importo)}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{fmtDate(r.data_emissione)}</td>
                  <td className="px-4 py-3 text-gray-500">{fmtDate(r.data_pagamento)}</td>
                  <td className="px-4 py-3"><StatusBadge stato={r.stato} /></td>
                  <td className="px-4 py-3 flex gap-1 justify-end">
                    <button className="btn-secondary btn-sm" onClick={() => openEdit(r)}>Modifica</button>
                    <button className="btn-danger btn-sm" onClick={() => del(r.id)}>Elimina</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {vista === 'commesse' && (
        <div className="space-y-3">
          {conFatture.length === 0 && <div className="card text-center text-gray-400 py-8">Nessuna commessa</div>}
          {conFatture.map(c => <AvanzamentoCommessa key={c.id} commessa={c} />)}
        </div>
      )}

      {modal && (
        <Modal title={modal.mode === 'new' ? 'Aggiungi fattura' : 'Modifica fattura'} onClose={() => setModal(null)} size="lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Commessa</label>
              {/* Cambiando commessa si lascia andare il cliente scelto a mano:
                  da qui in poi comanda la commessa. */}
              <select className="input" value={form.commessa_id || ''}
                      onChange={e => setForm(p => ({ ...p, commessa_id: e.target.value,
                                                    cliente_id: e.target.value ? '' : p.cliente_id }))}>
                <option value="">— Nessuna: fattura al cliente —</option>
                {commesse.map(c => <option key={c.id} value={c.id}>{c.numero} — {c.titolo}</option>)}
              </select>
            </div>
            <div><label className="label">N. Fattura</label><input className="input" {...f('numero_fattura')} /></div>
            {/* Non tutto quello che si fattura nasce da una commessa: un
                addebito una tantum, un conguaglio su lavori vecchi. Serve
                però sapere a chi è intestata. */}
            {!form.commessa_id && (
              <div className="col-span-2">
                <label className="label">Cliente *</label>
                <select className="input" value={form.cliente_id || ''}
                        onChange={e => setForm(p => ({ ...p, cliente_id: e.target.value }))}>
                  <option value="">— Seleziona il cliente —</option>
                  {clienti.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale || c.nome}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-0.5">
                  Fattura fuori commessa: entra nel riepilogo del mese e nei totali del
                  cliente, ma non scala nessun budget.
                </p>
              </div>
            )}
            <div className="col-span-2"><label className="label">Descrizione *</label><input className="input" {...f('descrizione')} /></div>
            {ordini?.ha_ordini && (
              <div className="col-span-2">
                <label className="label">Ordine del cliente</label>
                <select className="input" value={form.ordine_id || ''}
                        onChange={e => setForm(p => ({ ...p, ordine_id: e.target.value }))}>
                  <option value="">— Nessuno —</option>
                  {ordini.ordini.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.numero}{o.importo ? ` — ${fmt(o.importo)}` : ''}
                      {o.residuo !== null ? ` (residuo ${fmt(o.residuo)})` : ''}
                      {o.stato !== 'attivo' ? ` · ${o.stato}` : ''}
                    </option>
                  ))}
                </select>
                {(() => {
                  const sel = ordini.ordini.find(o => String(o.id) === String(form.ordine_id));
                  if (!sel || sel.residuo === null) {
                    return <p className="text-xs text-gray-400 mt-0.5">Il numero d'ordine va citato in fattura.</p>;
                  }
                  const val = Number(form.importo) || 0;
                  const oltre = val > sel.residuo + 0.01;
                  return (
                    <p className={`text-xs mt-0.5 ${oltre ? 'text-red-600' : 'text-gray-500'}`}>
                      {oltre
                        ? `Questo importo supera di ${fmt(val - sel.residuo)} quanto resta dell'ordine.`
                        : `Dopo questa fattura resteranno ${fmt(sel.residuo - val)} sull'ordine.`}
                    </p>
                  );
                })()}
              </div>
            )}
            <div><label className="label">Importo (€) *</label><CampoImporto className="input" value={form.importo} onChange={v => setForm(p => ({ ...p, importo: v }))} /></div>
            <div>
              <label className="label">Stato</label>
              <select className="input" value={form.stato} onChange={e => setForm(p => ({ ...p, stato: e.target.value }))}>
                {STATI.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div><label className="label">Data emissione</label><input className="input" type="date" {...f('data_emissione')} /></div>
            <div><label className="label">Data scadenza</label><input className="input" type="date" {...f('data_scadenza')} /></div>
            <div>
              <label className="label">Mese di competenza</label>
              <SceltaMese value={form.competenza} vuoto="— dedotto dalla data di emissione —"
                          onChange={v => setForm(p => ({ ...p, competenza: v }))} />
              <p className="text-xs text-gray-400 mt-0.5">
                Il mese a cui si riferisce l'importo. Lascialo vuoto e viene dedotto
                dalla data di emissione.
              </p>
            </div>
            <div><label className="label">Data pagamento</label><input className="input" type="date" {...f('data_pagamento')} /></div>
            <div className="col-span-2"><label className="label">Note</label><textarea className="input" rows={2} {...f('note')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo...' : 'Salva'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
